import React from "react";
import styled from "@emotion/styled";
import { useNavigate } from "react-router-dom";
import {
  Button,
  CloseButton,
  FlexRow,
  Form,
  H2,
  H3,
  Label,
  Select,
  TextArea,
} from "atoms";
import { useCreateTicket, ticketCategories } from "../api/useCreateTicket";
import type { CreateTicketFormData } from "../api/useCreateTicket";

interface ReportProjectProps {
  reviewableId: string;
  reviewableType: string;
  isOpen: boolean;
  close: () => void;
}

function CreateTicket({
  reviewableId,
  reviewableType,
  isOpen,
  close,
}: ReportProjectProps) {
  const navigate = useNavigate();

  const { createTicket, isSuccess, isError, isLoading } = useCreateTicket(
    reviewableId,
    reviewableType
  );

  const [formData, setFormData] = React.useState<CreateTicketFormData>({
    category: ticketCategories[0],
    notes: "",
  });
  const onChange: React.ChangeEventHandler<
    HTMLTextAreaElement | HTMLSelectElement
  > = ({ target }) => {
    setFormData({ ...formData, [target.name]: target.value });
  };

  const onSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    createTicket(formData);
  };

  if (!isOpen) return null;

  const onClose = () => {
    close();
    navigate("#");
  };

  return (
    <>
      {isSuccess ? (
        <>
          <FlexRow justifyContent="space-between" alignItems="flex-start">
            <H2 size="md" as="h3" textTransform="uppercase">
              Thanks! We&apos;ll take a look.
            </H2>
            <CloseButton aria-label="close report form" onClick={onClose} />
          </FlexRow>

          <Button onClick={close}>Close</Button>
        </>
      ) : (
        <Form onSubmit={onSubmit}>
          <FlexRow justifyContent="space-between" alignItems="flex-start">
            <H2 size="md" as="h3" textTransform="uppercase">
              Report a problem with this&nbsp;profile
            </H2>
            <CloseButton aria-label="close report form" onClick={onClose} />
          </FlexRow>

          <Label>
            Category
            <Select
              id="category"
              name="category"
              value={formData.category}
              onChange={onChange}
            >
              {ticketCategories.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </Select>
          </Label>

          <Label>
            Notes
            <TextArea
              id="notes"
              name="notes"
              onChange={onChange}
              required
              value={formData.notes}
            />
          </Label>
          <FlexRow>
            <Button size="md" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="md"
              variant="primary"
              type="submit"
              disabled={isLoading}
              arrow="r"
            >
              Submit
            </Button>

            {isLoading && <em>Sending...</em>}
          </FlexRow>
        </Form>
      )}

      {isError && <FormSubTitle>Error saving...</FormSubTitle>}
    </>
  );
}

const FormSubTitle = styled(H3)(({ theme }) => ({
  ...theme.fonts.body,
  fontWeight: "bold",
  marginBottom: "1rem",
}));

export { CreateTicket };
